import { motion } from "framer-motion";

/**
 * SceneShell — the base layout for every scene.
 * Provides background gradient (default `forest-hero`), grain, vignette, padding, and content alignment.
 *
 * Variants:
 *   - "forest"   (default): the deep forest hero gradient
 *   - "ink"      : near-black with subtle radial glow
 *   - "gold"     : Social Week gold-glow gradient
 *   - "custom"   : pass `bgClassName` / `bgStyle` to override
 */
export const SceneShell = ({
  children,
  variant = "forest",
  bgClassName = "",
  bgStyle,
  className = "",
  align = "center",
  contentClassName = "",
  showGrain = true,
  showVignette = true,
  "data-testid": testId,
}) => {
  const variantClass =
    variant === "gold"
      ? "bg-gold-glow"
      : variant === "ink"
        ? "bg-ink-veil"
        : variant === "custom"
          ? bgClassName
          : "bg-forest-hero";

  const alignClass =
    align === "left"
      ? "items-start text-left"
      : align === "right"
        ? "items-end text-right"
        : "items-center text-center";

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute inset-0 stage ${variantClass} ${
        showGrain ? "grain" : ""
      } ${showVignette ? "vignette" : ""} ${className}`}
      style={bgStyle}
      data-testid={testId}
    >
      <motion.div
        initial={{ scale: 1.02 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className={`relative h-full w-full flex flex-col justify-center px-8 sm:px-16 lg:px-28 ${alignClass} ${contentClassName}`}
      >
        {children}
      </motion.div>
    </motion.section>
  );
};

export default SceneShell;
